import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { ELEMENTO_COLUMNS, ELEMENTO_COLUMNS_BY_ID } from '@/lib/inventario/elementoColumns';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface Prefs {
  order: string[];
  hidden: string[];
}

interface Options {
  /** Sólo se exponen columnas cuyo id esté en este Set. */
  allowedIds?: Set<string>;
}

const PREF_KEY = 'elementos_columns_v1';

const buildDefaults = (): Prefs => ({
  order: ELEMENTO_COLUMNS.map((c) => c.id),
  hidden: ELEMENTO_COLUMNS.filter((c) => !c.defaultVisible).map((c) => c.id),
});

function sanitize(p: Partial<Prefs> | null | undefined): Prefs {
  const known = new Set(ELEMENTO_COLUMNS.map((c) => c.id));
  const order = (p?.order ?? []).filter((id) => known.has(id));
  for (const c of ELEMENTO_COLUMNS) if (!order.includes(c.id)) order.push(c.id);
  const hidden = (p?.hidden ?? []).filter((id) => known.has(id));
  return { order, hidden };
}

// ---------- store global compartido entre todas las instancias del hook ----------

type Listener = () => void;

interface Store {
  state: Prefs;
  listeners: Set<Listener>;
  loaded: boolean;
  loading: boolean;
  saveTimer?: ReturnType<typeof setTimeout>;
}

const stores: Record<string, Store> = {};

function lsKey(userId: string) {
  return `elementos-cols:${userId}`;
}

function getStore(userId: string): Store {
  let store = stores[userId];
  if (!store) {
    let initial = buildDefaults();
    if (typeof window !== 'undefined') {
      try {
        const raw = window.localStorage.getItem(lsKey(userId));
        if (raw) initial = sanitize(JSON.parse(raw));
      } catch { /* ignore */ }
    }
    store = { state: initial, listeners: new Set(), loaded: false, loading: false };
    stores[userId] = store;
  }
  return store;
}

function notify(store: Store) {
  store.listeners.forEach((l) => l());
}

async function loadFromServer(userId: string) {
  const store = getStore(userId);
  if (store.loaded || store.loading || userId === 'anon') return;
  store.loading = true;
  try {
    const { data, error } = await supabase
      .from('user_preferences')
      .select('value')
      .eq('user_id', userId)
      .eq('key', PREF_KEY)
      .maybeSingle();
    if (!error && data?.value) {
      const next = sanitize(data.value as Partial<Prefs>);
      store.state = next;
      try { window.localStorage.setItem(lsKey(userId), JSON.stringify(next)); } catch { /* ignore */ }
      notify(store);
    }
    store.loaded = true;
  } finally {
    store.loading = false;
  }
}

function persistToServer(userId: string, value: Prefs) {
  if (userId === 'anon') return;
  void supabase
    .from('user_preferences')
    .upsert(
      { user_id: userId, key: PREF_KEY, value: value as any },
      { onConflict: 'user_id,key' },
    );
}

function setPrefsFor(userId: string, next: Prefs) {
  const store = getStore(userId);
  store.state = next;
  try { window.localStorage.setItem(lsKey(userId), JSON.stringify(next)); } catch { /* ignore */ }
  notify(store);
  // Debounce server save
  if (store.saveTimer) clearTimeout(store.saveTimer);
  store.saveTimer = setTimeout(() => persistToServer(userId, next), 400);
}

function subscribeStore(userId: string, listener: Listener) {
  const store = getStore(userId);
  store.listeners.add(listener);
  return () => { store.listeners.delete(listener); };
}

/**
 * Preferencias por usuario para columnas de la tabla de elementos.
 * Se persisten en Supabase (`user_preferences`) y se cachean en localStorage
 * para que el cambio se vea al instante y se sincronice entre dispositivos.
 */
export function useElementoColumnPrefs(options: Options = {}) {
  const { allowedIds } = options;
  const { perfil } = useAuth();
  const userId = perfil?.user_id ?? 'anon';

  const subscribe = useCallback((l: Listener) => subscribeStore(userId, l), [userId]);
  const getSnapshot = useCallback(() => getStore(userId).state, [userId]);
  const prefs = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    void loadFromServer(userId);
  }, [userId]);

  const toggle = useCallback(
    (id: string) => {
      const current = getStore(userId).state;
      const hidden = new Set(current.hidden);
      if (hidden.has(id)) hidden.delete(id);
      else hidden.add(id);
      setPrefsFor(userId, { ...current, hidden: [...hidden] });
    },
    [userId],
  );

  const orderedColumns = useMemo(() => {
    const cols = prefs.order
      .map((id) => ELEMENTO_COLUMNS_BY_ID[id])
      .filter(Boolean);
    if (!allowedIds) return cols;
    return cols.filter((c) => allowedIds.has(c.id));
  }, [prefs.order, allowedIds]);

  const move = useCallback(
    (id: string, dir: -1 | 1) => {
      const current = getStore(userId).state;
      const order = [...current.order];
      const i = order.indexOf(id);
      if (i < 0) return;

      let j = i + dir;
      while (j >= 0 && j < order.length) {
        if (!allowedIds || allowedIds.has(order[j])) break;
        j += dir;
      }
      if (j < 0 || j >= order.length) return;

      [order[i], order[j]] = [order[j], order[i]];
      setPrefsFor(userId, { ...current, order });
    },
    [userId, allowedIds],
  );

  const reset = useCallback(() => setPrefsFor(userId, buildDefaults()), [userId]);

  const visibleColumns = useMemo(
    () => orderedColumns.filter((c) => !prefs.hidden.includes(c.id)),
    [orderedColumns, prefs.hidden],
  );

  return { prefs, orderedColumns, visibleColumns, toggle, move, reset };
}
